import { FiLogOut, FiBell, FiMenu, FiX, FiInfo } from "react-icons/fi";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export default function Topbar({ 
  setSidebarOpen, 
  sidebarOpen, 
  setMode, 
  mode,
  sidebarCollapsed = false
}: {
  setSidebarOpen: (open: boolean) => void,
  sidebarOpen: boolean,
  setMode: (mode: 'light' | 'dark') => void,
  mode: 'light' | 'dark',
  sidebarCollapsed?: boolean,
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "User";
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  // Use collapsed state for desktop, sidebarOpen for mobile
  const sidebarWidth = isDesktop ? (sidebarCollapsed ? 72 : 256) : 0;

  const handleLogout = () => {
    const storedClientCode = localStorage.getItem("clientCode");
    localStorage.clear();
    if (storedClientCode && storedClientCode !== "admin") {
      navigate(`/${storedClientCode}/login`);
      return;
    }
    navigate("/admin/login");
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const palette = {
    topbarBg: '#fff',
    topbarText: '#1a237e',
    topbarShadow: '0 2px 8px 0 rgba(31, 38, 135, 0.06)',
    icon: '#1a237e',
    iconHover: '#e3e8ee',
  };

  return (
    <AppBar position="sticky" color="default" elevation={2} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: palette.topbarBg, boxShadow: palette.topbarShadow }}>
      <Toolbar sx={{
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: 64,
        px: { xs: 1, md: 3 },
        pl: isDesktop ? `${sidebarWidth}px` : 1,
        transition: 'padding-left 0.2s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Left: Hamburger/Close (mobile only) and Welcome */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          {!isDesktop && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              sx={{ mr: 1, color: palette.icon, '&:hover': { bgcolor: palette.iconHover } }}
            >
              {sidebarOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: palette.topbarText,
              letterSpacing: 1,
              whiteSpace: isDesktop ? 'nowrap' : 'normal',
              overflow: isDesktop ? 'hidden' : 'visible',
              textOverflow: isDesktop ? 'ellipsis' : 'unset',
              fontSize: { xs: 15, sm: 17, md: 19, lg: 20 },
              maxWidth: isDesktop ? 320 : '100%',
              ml: !isDesktop ? 0.5 : 0,
              flexShrink: 1,
              flexGrow: 1,
              lineHeight: 1.2,
            }}
            component="div"
          >
            Welcome {username}
          </Typography>
        </Box>
        {/* Right: Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton color="inherit" title="Info" aria-label="Info" onClick={() => window.open('https://chat.openai.com/', '_blank', 'noopener') } sx={{ color: palette.icon, '&:hover': { bgcolor: palette.iconHover } }}>
            <FiInfo />
          </IconButton>
          <IconButton color="inherit" title="Notifications" aria-label="Notifications" sx={{ color: palette.icon, '&:hover': { bgcolor: palette.iconHover } }}>
            <FiBell />
          </IconButton>
          <IconButton onClick={handleMenuOpen} sx={{ p: 0, ml: 1, color: palette.icon, '&:hover': { bgcolor: palette.iconHover } }} aria-label="Open user menu">
            <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 'bold', width: 36, height: 36, fontSize: 18 }}>
              {username.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 3,
              sx: { borderRadius: 3, minWidth: 180, mt: 1 }
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main', fontWeight: 500 }}>
              <FiLogOut style={{ marginRight: 8 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
} 